import {
  type MutateOptions,
  type MutationFunctionContext,
  type UseMutateAsyncFunction,
  type UseMutateFunction,
  type UseMutationOptions as UseMutationReactOptions,
  type UseMutationResult,
  useMutation as useMutationReact,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useHandleNetworkError } from '#error-handlers';

export type UseMutationOptions<
  TData = unknown,
  TError extends AxiosError | Error = AxiosError,
  TVariables = void,
  TOnMutateResult = unknown,
> = {
  defaultMutateOnError?: MutateOptions<TData, TError, TVariables, TOnMutateResult>['onError'];
} & UseMutationReactOptions<TData, TError, TVariables, TOnMutateResult>;

export const useMutation = <
  TData = unknown,
  TVariables = void,
  TError extends AxiosError | Error = AxiosError,
  TOnMutateResult = unknown,
>({
  defaultMutateOnError,
  ...options
}: UseMutationOptions<TData, TError, TVariables, TOnMutateResult>): UseMutationResult<
  TData,
  TError,
  TVariables,
  TOnMutateResult
> => {
  const handleNetworkError = useHandleNetworkError();

  const defaultMutateOnErrorHandler = useCallback(
    (error: TError) => {
      handleNetworkError({ error });
    },
    [handleNetworkError],
  );

  const {
    mutate: mutateReactQuery,
    mutateAsync: mutateAsyncReactQuery,
    ...rest
  } = useMutationReact<TData, TError, TVariables, TOnMutateResult>(options);

  const callbackOnError = useCallback(
    (
      error: TError,
      variables: TVariables,
      onMutateResult: TOnMutateResult | undefined,
      context: MutationFunctionContext,
    ) => {
      const onError = defaultMutateOnError ?? defaultMutateOnErrorHandler;

      onError(error, variables, onMutateResult, context);
    },
    [defaultMutateOnError, defaultMutateOnErrorHandler],
  );

  const mutate = useCallback(
    (...mutateArguments: Parameters<UseMutateFunction<TData, TError, TVariables, TOnMutateResult>>) => {
      const mutateOptions = mutateArguments[1];
      mutateArguments[1] = {
        ...mutateOptions,
        onError: mutateOptions?.onError ?? callbackOnError,
      };
      mutateReactQuery(...mutateArguments);
    },
    [callbackOnError, mutateReactQuery],
  );

  const mutateAsync = useCallback(
    async (...mutateArguments: Parameters<UseMutateAsyncFunction<TData, TError, TVariables, TOnMutateResult>>) => {
      const mutateOptions = mutateArguments[1];
      mutateArguments[1] = {
        ...mutateOptions,
        onError: mutateOptions?.onError ?? callbackOnError,
      };
      return mutateAsyncReactQuery(...mutateArguments);
    },
    [callbackOnError, mutateAsyncReactQuery],
  );

  return {
    mutate,
    mutateAsync,
    ...rest,
  };
};
